import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useBranch } from '../../context/BranchContext';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Modal, Form, Row, Col, Tabs, Tab, Table, Spinner } from 'react-bootstrap';
import { Plus, Pencil, Trash2, GripVertical, CheckCircle, XCircle, Eye, ArrowRepeat, Telephone, StarFill, Star, PersonCheck, ClockHistory, Images } from 'react-bootstrap-icons';
import JoditEditor from 'jodit-react';
import { useAdminMenu } from '../../context/AdminMenuContext';
import { cloudinaryService, employeeAPI, branchAPI } from '../../services';
import { menuItemAPI } from '../../services/menuItem';
import { toYoutubeEmbedUrl } from '../../utils/youtube';
import { getAvatarInitials } from '../../utils/employeeName';
import { toast } from 'react-toastify';

const ImagePreview = ({ src, alt, size = 56 }) => {
  const [hasError, setHasError] = useState(false);
  const backgroundColor = '#d3b090';

  useEffect(() => {
    setHasError(false);
  }, [src]);

  return (
    <div
      className="border rounded bg-light d-flex align-items-center justify-content-center"
      style={{
        inlineSize: typeof size === 'number' ? `${size}px` : size,
        blockSize: typeof size === 'number' ? `${size}px` : size,
        overflow: 'hidden',
        background: backgroundColor,
      }}
    >
      {!hasError && src ? (
        <img
          src={src}
          alt={alt}
          className="w-100 h-100"
          style={{ objectFit: 'cover' }}
          onError={() => setHasError(true)}
        />
      ) : (
        <span className="text-white-50 small">No Image</span>
      )}
    </div>
  );
};

const MenuItemModal = ({ show, onHide, menuItem, categories, modifierGroups, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: 0,
    categoryId: '',
    isAvailable: true,
    isFeatured: false,
    requiresTraining: false,
    tags: '',
    enabledModifierGroupIds: [],
    image: '',
    createdBy: '',
    videoUrls: [],
    recipeHtml: '',
  });
  const [videoInputs, setVideoInputs] = useState(['']);
  const [videoErrors, setVideoErrors] = useState(['']);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [employees, setEmployees] = useState([]);
  const fileInputRef = useRef(null);
  const joditConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    toolbarAdaptive: false,
    buttons: [
      'bold', 'italic', 'underline', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', '|',
      'paragraph', '|',
      'image', 'link', '|',
      'undo', 'redo', '|',
      'fullsize',
    ],
    language: 'vi',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    uploader: { insertImageAsBase64URI: true },
  }), []);

  // Fetch active employees khi modal mở
  useEffect(() => {
    if (!show) return;
    employeeAPI.getEmployees({ status: 'ACTIVE', limit: 100 })
      .then((res) => setEmployees(res.data || []))
      .catch(() => setEmployees([]));
  }, [show]);

  useEffect(() => {
    if (menuItem) {
      const existingUrls = menuItem.videoUrls || menuItem.video_urls || [];
      setFormData({
        name: menuItem.name,
        slug: menuItem.slug,
        description: menuItem.description,
        price: menuItem.price,
        categoryId: menuItem.categoryId,
        isAvailable: menuItem.isAvailable,
        isFeatured: menuItem.isFeatured,
        requiresTraining: menuItem.requires_training ?? false,
        tags: menuItem.tags?.join(', ') || '',
        enabledModifierGroupIds: menuItem.enabledModifierGroupIds || [],
        image: menuItem.image || '',
        createdBy: menuItem.createdBy || '',
        videoUrls: existingUrls,
        recipeHtml: menuItem.recipeHtml || menuItem.recipe_html || '',
      });
      setVideoInputs(existingUrls.length > 0 ? [...existingUrls, ''] : ['']);
      setVideoErrors(existingUrls.length > 0 ? [...existingUrls.map(() => ''), ''] : ['']);
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        price: 0,
        categoryId: categories[0]?.id || '',
        isAvailable: true,
        isFeatured: false,
        tags: '',
        enabledModifierGroupIds: [],
        image: '/images/placeholder.jpg',
        createdBy: '',
        videoUrls: [],
        recipeHtml: '',
      });
      setVideoInputs(['']);
      setVideoErrors(['']);
    }
  }, [menuItem, show, categories]);

  const handleVideoInputChange = (idx, value) => {
    const updated = [...videoInputs];
    updated[idx] = value;
    setVideoInputs(updated);
    // Clear error while typing
    const errs = [...videoErrors];
    errs[idx] = '';
    setVideoErrors(errs);
  };

  const handleVideoInputBlur = (idx) => {
    const raw = videoInputs[idx]?.trim();
    if (!raw) {
      // Empty slot — remove from videoUrls
      const errs = [...videoErrors];
      errs[idx] = '';
      setVideoErrors(errs);
      const urls = videoInputs
        .filter((_, i) => i !== idx)
        .map((v) => toYoutubeEmbedUrl(v?.trim()))
        .filter(Boolean);
      setFormData((prev) => ({ ...prev, videoUrls: urls }));
      return;
    }
    const embed = toYoutubeEmbedUrl(raw);
    const errs = [...videoErrors];
    if (!embed) {
      errs[idx] = 'Link YouTube không hợp lệ';
      setVideoErrors(errs);
      return;
    }
    errs[idx] = '';
    setVideoErrors(errs);
    // Update the input to show the canonical embed URL
    const updatedInputs = [...videoInputs];
    updatedInputs[idx] = embed;
    setVideoInputs(updatedInputs);
    // Rebuild videoUrls from all valid inputs
    const urls = updatedInputs.map((v) => toYoutubeEmbedUrl(v?.trim())).filter(Boolean);
    setFormData((prev) => ({ ...prev, videoUrls: urls }));
  };

  const handleAddVideoSlot = () => {
    setVideoInputs((prev) => [...prev, '']);
    setVideoErrors((prev) => [...prev, '']);
  };

  const handleRemoveVideo = (idx) => {
    const updatedInputs = videoInputs.filter((_, i) => i !== idx);
    const updatedErrors = videoErrors.filter((_, i) => i !== idx);
    setVideoInputs(updatedInputs.length > 0 ? updatedInputs : ['']);
    setVideoErrors(updatedErrors.length > 0 ? updatedErrors : ['']);
    const urls = updatedInputs.map((v) => toYoutubeEmbedUrl(v?.trim())).filter(Boolean);
    setFormData((prev) => ({ ...prev, videoUrls: urls }));
  };

  const handleNameChange = (name) => {
    const slug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    setFormData({ ...formData, name, slug });
  };

  const toggleModifierGroup = (groupId) => {
    setFormData((prev) => ({
      ...prev,
      enabledModifierGroupIds: prev.enabledModifierGroupIds.includes(groupId)
        ? prev.enabledModifierGroupIds.filter((id) => id !== groupId)
        : [...prev.enabledModifierGroupIds, groupId],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const tags = formData.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t);
    onSave({ ...formData, tags });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadError('');
      setUploadingImage(true);
      setUploadProgress(0);

      const response = await cloudinaryService.uploadImage(file, {
        folder: 'menu-items',
        compression: {
          maxSizeMB: 0.8,
          maxWidthOrHeight: 1200,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(percent);
        },
      });

      setFormData((prev) => ({
        ...prev,
        image: response.secure_url,
      }));
      toast.success('Upload ảnh lên server thành công!');
    } catch (error) {
      console.error('Image upload error:', error);
      setUploadError(error.message || 'Upload ảnh thất bại. Vui lòng thử lại.');
      toast.error('Upload ảnh thất bại');
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleResetImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: '/images/placeholder.jpg',
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{menuItem ? 'Sửa món' : 'Thêm món mới'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxBlockSize: '70vh', overflowY: 'auto' }}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tên món *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  placeholder="Nhập tên món"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Slug *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  placeholder="slug-mon-an"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Mô tả *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder="Nhập mô tả món"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Giá (VNĐ) *</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  required
                  min="0"
                  step="1000"
                  placeholder="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Danh mục *</Form.Label>
                <Form.Select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  required
                >
                  <option value="">Chọn danh mục</option>
                  {categories.map((cat) => (
                    <option
                      key={cat.id}
                      value={cat.id}
                    >
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="align-items-start">
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Ảnh món (URL)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.image || ''}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/images/coffee.jpg"
                />
                <Form.Text className="text-muted">Dán link ảnh hoặc upload lên CDN rồi nhập URL tại đây.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Hoặc upload ảnh lên server</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleResetImage}
                    disabled={uploadingImage}
                  >
                    Đặt lại
                  </Button>
                </div>
                {uploadingImage && (
                  <Form.Text className="text-primary d-block mt-1">Đang upload... {uploadProgress}%</Form.Text>
                )}
                {uploadError && <Form.Text className="text-danger d-block mt-1">{uploadError}</Form.Text>}
              </Form.Group>
            </Col>
            <Col
              md={4}
              className="mb-3"
            >
              <div className="border rounded bg-light d-flex flex-column align-items-center p-2">
                <small className="text-muted mb-2">Preview</small>
                <div style={{ inlineSize: '160px', blockSize: '160px' }}>
                  <ImagePreview
                    src={formData.image || '/images/placeholder.jpg'}
                    alt={formData.name || 'Preview'}
                    size="100%"
                  />
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tags (phân cách bằng dấu phẩy)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Phổ biến, Mới, Đặc biệt"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nhân viên sáng tạo <small className="text-muted">(tùy chọn, dùng chia sẻ doanh thu)</small>
                </Form.Label>
                <Form.Select
                  value={formData.createdBy}
                  onChange={(e) => setFormData({ ...formData, createdBy: e.target.value })}
                >
                  <option value="">-- Không có --</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name || emp.fullName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Card className="mb-3">
            <Card.Header className="bg-light">
              <h6 className="mb-0">📖 Công thức / Hướng dẫn làm</h6>
            </Card.Header>
            <Card.Body>
              <JoditEditor
                value={formData.recipeHtml}
                config={joditConfig}
                onBlur={(html) => setFormData((prev) => ({ ...prev, recipeHtml: html }))}
              />
              <Form.Text className="text-muted">Nội dung công thức sẽ hiển thị cho nhân viên trong trang training.</Form.Text>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <h6 className="mb-0">🎬 Video hướng dẫn (tối đa 5)</h6>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleAddVideoSlot}
                disabled={formData.videoUrls.length >= 5}
              >
                + Thêm video
              </Button>
            </Card.Header>
            <Card.Body>
              <Form.Text className="text-muted d-block mb-2">
                Upload video lên YouTube (chế độ <strong>Unlisted</strong> hoặc Public), sau đó dán link vào đây.
                Hỗ trợ: youtube.com/watch?v=…, youtu.be/…, link embed, link Shorts.
              </Form.Text>
              {videoInputs.map((url, idx) => {
                const embedUrl = toYoutubeEmbedUrl(url?.trim());
                const videoId = embedUrl ? embedUrl.replace('https://www.youtube.com/embed/', '') : null;
                return (
                  <div key={idx} className="mb-3">
                    <div className="d-flex gap-2 align-items-start">
                      <div className="flex-grow-1">
                        <Form.Control
                          type="text"
                          value={url}
                          onChange={(e) => handleVideoInputChange(idx, e.target.value)}
                          onBlur={() => handleVideoInputBlur(idx)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          isInvalid={!!videoErrors[idx]}
                        />
                        {videoErrors[idx] && (
                          <Form.Control.Feedback type="invalid">{videoErrors[idx]}</Form.Control.Feedback>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleRemoveVideo(idx)}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Xoá
                      </Button>
                    </div>
                    {videoId && (
                      <div className="mt-2">
                        <img
                          src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                          alt={`Thumbnail video ${idx + 1}`}
                          style={{ blockSize: '80px', borderRadius: '4px', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="bg-light">
              <h6 className="mb-0">Tuỳ chọn món</h6>
            </Card.Header>
            <Card.Body>
              {modifierGroups
                .filter((group) => group.isActive)
                .map((group) => (
                  <Form.Check
                    key={group.id}
                    type="switch"
                    id={`mod-${group.id}`}
                    label={
                      <div>
                        <div>{group.name}</div>
                        <small className="text-muted">
                          {group.type === 'single' ? 'Chọn 1' : 'Chọn nhiều'} •{' '}
                          {group.required ? 'Bắt buộc' : 'Tuỳ chọn'}
                        </small>
                      </div>
                    }
                    checked={formData.enabledModifierGroupIds.includes(group.id)}
                    onChange={() => toggleModifierGroup(group.id)}
                    className="mb-2"
                  />
                ))}
              {modifierGroups.filter((g) => g.isActive).length === 0 && (
                <small className="text-muted">Chưa có tuỳ chọn nào. Vui lòng tạo tuỳ chọn trước.</small>
              )}
            </Card.Body>
          </Card>

          <Form.Group className="mb-2">
            <Form.Check
              type="switch"
              id="item-available"
              label="Còn hàng"
              checked={formData.isAvailable}
              onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
            />
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="switch"
              id="item-featured"
              label="Nổi bật"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
            />
          </Form.Group>

          <Form.Group className="mt-3 p-3 bg-light rounded border">
            <Form.Check
              type="switch"
              id="item-requires-training"
              label="📚 Cần đào tạo nhân viên"
              checked={formData.requiresTraining}
              onChange={(e) => setFormData({ ...formData, requiresTraining: e.target.checked })}
            />
            <Form.Text className="text-muted d-block mt-1">
              Bật để theo dõi nhân viên biết làm món này. Khi không có NV biết làm trong ca (nếu bật auto-lock), món sẽ tự ẩn trên menu.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            type="submit"
          >
            {menuItem ? 'Cập nhật' : 'Thêm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ---- Pending items panel ----

const APPROVAL_STATUS_LABELS = {
  DRAFT: { label: 'Nháp', bg: 'secondary' },
  PENDING: { label: 'Chờ duyệt', bg: 'warning' },
  APPROVED: { label: 'Đã duyệt', bg: 'success' },
  REJECTED: { label: 'Từ chối', bg: 'danger' },
};

// ---- Release Hub Modal ----
const STEP_LABELS = {
  recipe: '📋 Công thức tồn kho',
  training_enabled: '🎓 Bật training',
};
const formatStepLabel = (step, availableBranches) => {
  if (STEP_LABELS[step]) return STEP_LABELS[step];
  if (step.startsWith('branch_menu:')) {
    const b = (availableBranches || []).find(b => b.id === step.slice('branch_menu:'.length));
    return `🏪 Menu chi nhánh: ${b?.name || 'chi nhánh'}`;
  }
  if (step.startsWith('training_released:')) {
    const b = (availableBranches || []).find(b => b.id === step.slice('training_released:'.length));
    return `📤 Training cho: ${b?.name || 'chi nhánh'}`;
  }
  return step;
};

const ReleaseHubModal = ({ show, onHide, menuItemId, menuItemName, onGoToRecipe }) => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [enableTraining, setEnableTraining] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [results, setResults] = useState([]);

  const loadStatus = useCallback(async () => {
    if (!menuItemId) return;
    setLoadingStatus(true);
    try {
      const res = await menuItemAPI.getReleaseStatus(menuItemId);
      setStatus(res);
      // Pre-select branches chưa có trong menu
      const inMenu = new Set((res.steps?.branch_menu?.branches || []).map((b) => b.branch_id));
      setSelectedBranches((res.available_branches || []).filter((b) => !inMenu.has(b.id)).map((b) => b.id));
      setEnableTraining(!res.steps?.training_enabled?.done);
    } catch (err) {
      toast.error(err?.message || 'Không thể tải trạng thái release');
    } finally {
      setLoadingStatus(false);
    }
  }, [menuItemId]);

  useEffect(() => {
    if (show) { setResults([]); loadStatus(); }
  }, [show, loadStatus]);

  const toggleBranch = (id) =>
    setSelectedBranches((prev) => prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]);

  const handleRelease = async () => {
    setReleasing(true);
    setResults([]);
    try {
      const payload = {
        branch_ids: selectedBranches.length > 0 ? selectedBranches : undefined,
        enable_training: enableTraining || undefined,
        training_branch_ids: enableTraining && selectedBranches.length > 0 ? selectedBranches : undefined,
      };
      const steps = await menuItemAPI.releaseAll(menuItemId, payload);
      const list = Array.isArray(steps) ? steps : [];
      setResults(list);
      const failed = list.filter((s) => !s.ok);
      if (failed.length === 0) {
        toast.success('Release hoàn tất!');
        loadStatus();
      } else {
        toast.warning(`${failed.length} bước thất bại`);
        loadStatus();
      }
    } catch (err) {
      toast.error(err?.message || 'Release thất bại');
    } finally {
      setReleasing(false);
    }
  };

  const steps = status?.steps;
  const allDone = status?.all_done;

  const StepRow = ({ done, icon, label, detail, action }) => (
    <div className={`d-flex align-items-center gap-2 p-2 rounded mb-2 ${done ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : 'bg-light border'}`}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{done ? '✅' : '⬜'}</span>
      <div className="flex-grow-1">
        <div className="fw-medium small">{icon} {label}</div>
        {detail && <div className="text-muted" style={{ fontSize: 12 }}>{detail}</div>}
      </div>
      {action}
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>🚀 Release Hub — {menuItemName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingStatus ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /> Đang tải trạng thái...</div>
        ) : !status ? (
          <p className="text-muted">Không thể tải trạng thái.</p>
        ) : (
          <>
            {/* Checklist */}
            <h6 className="text-uppercase text-muted small mb-2">Trạng thái release</h6>
            <StepRow
              done={steps?.recipe?.done}
              icon="📋"
              label="Công thức tồn kho (BOM)"
              detail={steps?.recipe?.done ? `${steps.recipe.count} nguyên liệu đã khai báo` : 'Chưa khai báo'}
              action={!steps?.recipe?.done && onGoToRecipe ? (
                <Button size="sm" variant="outline-primary" style={{ whiteSpace: 'nowrap' }}
                  onClick={() => { onHide(); onGoToRecipe(menuItemId); }}>
                  → Khai báo
                </Button>
              ) : null}
            />
            <StepRow
              done={steps?.branch_menu?.done}
              icon="🏪"
              label="Thêm vào menu chi nhánh"
              detail={steps?.branch_menu?.done
                ? `Đã có tại: ${(steps.branch_menu.branches || []).map((b) => b.branch_name).join(', ')}`
                : 'Chưa có chi nhánh nào'}
            />
            <StepRow
              done={steps?.training_enabled?.done}
              icon="🎓"
              label="Bật training"
              detail={steps?.training_enabled?.done ? 'Đã bật requires_training' : 'Chưa bật'}
            />
            <StepRow
              done={steps?.training_released?.done}
              icon="📤"
              label="Phát hành training cho chi nhánh"
              detail={steps?.training_released?.done
                ? `Phát hành tại: ${(steps.training_released.branches || []).map((b) => b.branch_name).join(', ')}`
                : 'Chưa phát hành'}
            />

            {allDone ? (
              <div className="text-center py-3 text-success">
                <div style={{ fontSize: 36 }}>✅</div>
                <p className="fw-semibold mb-0">Tất cả bước release đã hoàn tất!</p>
              </div>
            ) : (
              <>
                <hr />
                <h6 className="text-uppercase text-muted small mb-2">Release tất cả</h6>
                <p className="text-muted small mb-3">Chọn chi nhánh và bấm Release — các bước đã hoàn tất sẽ được bỏ qua.</p>

                {/* Branch selector */}
                {(status.available_branches || []).length > 0 && (
                  <div className="mb-3">
                    <Form.Label className="fw-medium small">Thêm vào menu + training tại chi nhánh:</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {(status.available_branches || []).map((b) => {
                        const inMenu = (steps?.branch_menu?.branches || []).some((bm) => bm.branch_id === b.id);
                        return (
                          <Form.Check
                            key={b.id}
                            type="checkbox"
                            id={`branch-${b.id}`}
                            label={<span>{b.name}{inMenu && <Badge bg="success" className="ms-1" style={{ fontSize: 10 }}>✓ đã có</Badge>}</span>}
                            checked={selectedBranches.includes(b.id)}
                            onChange={() => toggleBranch(b.id)}
                            disabled={inMenu}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Training toggle */}
                {!steps?.training_enabled?.done && (
                  <Form.Check
                    className="mb-3"
                    type="checkbox"
                    id="enable-training"
                    label="Bật training cho món này"
                    checked={enableTraining}
                    onChange={(e) => setEnableTraining(e.target.checked)}
                  />
                )}

                {/* Results */}
                {results.length > 0 && (
                  <div className="mb-3">
                    <Form.Label className="fw-medium small">Kết quả:</Form.Label>
                    {results.map((r, i) => (
                      <div key={i} className={`d-flex gap-2 align-items-start p-2 rounded mb-1 ${r.ok ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                        <span style={{ flexShrink: 0 }}>{r.ok ? '✅' : '❌'}</span>
                        <div className="flex-grow-1">
                          <div className="fw-medium small">{formatStepLabel(r.step, status?.available_branches)}</div>
                          {(r.message || r.error) && (
                            <div className={`small ${r.ok ? 'text-success' : 'text-danger'}`}>{r.message || r.error}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Đóng</Button>
        {!allDone && !loadingStatus && (
          <Button variant="primary" onClick={handleRelease} disabled={releasing || (selectedBranches.length === 0 && !enableTraining)}>
            {releasing ? <><Spinner size="sm" className="me-1" />Đang release...</> : '🚀 Release ngay'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const PendingItemsPanel = ({ categories, onGoToRecipe }) => {
  const [pendingItems, setPendingItems] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [rejectModal, setRejectModal] = useState({ show: false, item: null });
  const [rejectReason, setRejectReason] = useState('');
  const [previewModal, setPreviewModal] = useState({ show: false, item: null });
  const [releaseModal, setReleaseModal] = useState({ show: false, id: null, name: '' });

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await menuItemAPI.getPendingMenuItems();
      setPendingItems(res?.data || []);
    } catch {
      setPendingItems([]);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const handleApprove = async (id, name) => {
    if (!window.confirm(`Duyệt món "${name}"?`)) return;
    try {
      await menuItemAPI.approveMenuItem(id, 'APPROVE');
      toast.success(`Đã duyệt "${name}" ✓`);
      loadPending();
      setReleaseModal({ show: true, id, name });
    } catch (err) {
      toast.error(err?.message || 'Duyệt thất bại');
    }
  };

  const openReject = (item) => {
    setRejectReason('');
    setRejectModal({ show: true, item });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.warning('Vui lòng nhập lý do'); return; }
    try {
      await menuItemAPI.approveMenuItem(rejectModal.item.id, 'REJECT', rejectReason.trim());
      toast.success('Đã từ chối món');
      setRejectModal({ show: false, item: null });
      loadPending();
    } catch (err) {
      toast.error(err?.message || 'Từ chối thất bại');
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          {pendingItems.length > 0 ? `${pendingItems.length} món đang chờ duyệt` : 'Không có món nào chờ duyệt'}
        </span>
        <Button variant="outline-secondary" size="sm" onClick={loadPending}>Tải lại</Button>
      </div>

      {loadingPending ? (
        <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
      ) : pendingItems.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <CheckCircle size={40} className="mb-2 text-success" />
          <p>Không có món nào đang chờ duyệt</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {pendingItems.map((item) => {
            const category = categories.find((c) => c.id === (item.category_id || item.categoryId));
            return (
              <div key={item.id} className="d-flex align-items-center gap-3 p-3 border rounded bg-light">
                <ImagePreview src={item.image || '/images/placeholder.jpg'} alt={item.name} size={56} />
                <div className="flex-grow-1">
                  <h6 className="mb-1">{item.name}</h6>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Badge bg="info" pill>{category?.name || 'N/A'}</Badge>
                    <span className="text-muted small">{(item.price || 0).toLocaleString('vi-VN')}đ</span>
                    {item.creator && <span className="text-muted small">👤 {item.creator.full_name}</span>}
                    {item.submitted_at && (
                      <span className="text-muted small">
                        🕐 {new Date(item.submitted_at).toLocaleDateString('vi-VN')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => setPreviewModal({ show: true, item })}>
                    <Eye size={16} />
                  </Button>
                  <Button variant="success" size="sm" onClick={() => handleApprove(item.id, item.name)}>
                    <CheckCircle size={16} className="me-1" />Duyệt
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => openReject(item)}>
                    <XCircle size={16} className="me-1" />Từ chối
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject modal */}
      <Modal show={rejectModal.show} onHide={() => setRejectModal({ show: false, item: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Từ chối món: {rejectModal.item?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Lý do từ chối *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Nhân viên sẽ thấy lý do này để chỉnh sửa lại..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectModal({ show: false, item: null })}>Hủy</Button>
          <Button variant="danger" onClick={handleReject}>Xác nhận từ chối</Button>
        </Modal.Footer>
      </Modal>

      {/* Preview modal */}
      <Modal show={previewModal.show} onHide={() => setPreviewModal({ show: false, item: null })} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Chi tiết: {previewModal.item?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewModal.item && (
            <div>
              {previewModal.item.image && (
                <img src={previewModal.item.image} alt={previewModal.item.name} className="w-100 rounded mb-3" style={{ maxHeight: 240, objectFit: 'cover' }} />
              )}
              <Row className="mb-2">
                <Col><strong>Giá:</strong> {(previewModal.item.price || 0).toLocaleString('vi-VN')}đ</Col>
                <Col><strong>Người tạo:</strong> {previewModal.item.creator?.full_name || '—'}</Col>
              </Row>
              {previewModal.item.description && <p><strong>Mô tả:</strong> {previewModal.item.description}</p>}
              {previewModal.item.recipe_html && (
                <div className="mb-2">
                  <strong>Công thức:</strong>
                  <div className="border rounded p-2 mt-1 small" dangerouslySetInnerHTML={{ __html: previewModal.item.recipe_html }} />
                </div>
              )}
              {(previewModal.item.video_urls || []).length > 0 && (
                <div className="mb-2">
                  <strong>Video:</strong>
                  {previewModal.item.video_urls.map((url, i) => (
                    <p key={i} className="mb-0">
                      <a href={url} target="_blank" rel="noopener noreferrer">Video {i + 1}: {url}</a>
                    </p>
                  ))}
                </div>
              )}
              {previewModal.item.supplier_info && (
                <p><strong>Nguồn hàng:</strong> {previewModal.item.supplier_info}</p>
              )}
              {previewModal.item.contact_employee && (
                <p><strong>Liên hệ học món:</strong> {previewModal.item.contact_employee.full_name}</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => { handleApprove(previewModal.item.id, previewModal.item.name); setPreviewModal({ show: false, item: null }); }}>
            <CheckCircle size={16} className="me-1" />Duyệt
          </Button>
          <Button variant="outline-danger" onClick={() => { setPreviewModal({ show: false, item: null }); openReject(previewModal.item); }}>
            <XCircle size={16} className="me-1" />Từ chối
          </Button>
          <Button variant="secondary" onClick={() => setPreviewModal({ show: false, item: null })}>Đóng</Button>
        </Modal.Footer>
      </Modal>

      <ReleaseHubModal
        show={releaseModal.show}
        onHide={() => setReleaseModal({ show: false, id: null, name: '' })}
        menuItemId={releaseModal.id}
        menuItemName={releaseModal.name}
        onGoToRecipe={onGoToRecipe}
      />
    </>
  );
};

// ---- Main component ----

const AdminMenuItems = () => {
  const {
    categories,
    menuItems,
    modifierGroups,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    fetchMenuItems,
    loading,
  } = useAdminMenu();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterApproval, setFilterApproval] = useState('all');
  const [releaseModal, setReleaseModal] = useState({ show: false, id: null, name: '' });
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();

  const handleGoToRecipe = useCallback((itemId) => {
    navigate(`/inventory/recipes?menu_item_id=${itemId}`);
  }, [navigate]);

  // Fetch menu items when filters change
  useEffect(() => {
    const params = {};
    if (filterCategory !== 'all') params.category_id = filterCategory;
    if (filterApproval !== 'all') params.approval_status = filterApproval;
    fetchMenuItems(params);
  }, [filterCategory, filterApproval, fetchMenuItems]);

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      let savedId;
      if (editingItem) {
        await updateMenuItem(editingItem.id, formData);
        savedId = editingItem.id;
      } else {
        const newItem = {
          ...formData,
          image: formData.image || '/images/placeholder.jpg',
          displayOrder: menuItems.length + 1,
        };
        const res = await createMenuItem(newItem);
        savedId = (res?.data || res)?.id;
      }
      // requires_training dùng endpoint riêng (không nằm trong update body chính)
      if (savedId) {
        const prevRequiresTraining = editingItem?.requires_training ?? false;
        if (formData.requiresTraining !== prevRequiresTraining) {
          await menuItemAPI.toggleRequiresTraining(savedId, formData.requiresTraining);
        }
      }
      fetchMenuItems();
      setShowModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving menu item:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc muốn xóa món này?')) {
      try {
        await deleteMenuItem(id);
      } catch (error) {
        console.error('Error deleting menu item:', error);
      }
    }
  };

  return (
    <>
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3" mountOnEnter>
        <Tab eventKey="all" title="Tất cả món">
      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h4 className="mb-1">Danh sách món</h4>
              <small className="text-muted">Quản lý các món trong menu</small>
            </div>
            <div className="d-flex gap-2 w-100 w-md-auto flex-wrap">
              <Form.Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                size="sm"
                style={{ maxInlineSize: '180px' }}
              >
                <option value="all">Tất cả danh mục</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </Form.Select>
              <Form.Select
                value={filterApproval}
                onChange={(e) => setFilterApproval(e.target.value)}
                size="sm"
                style={{ maxInlineSize: '160px' }}
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="APPROVED">Đã duyệt</option>
                <option value="PENDING">Chờ duyệt</option>
                <option value="DRAFT">Nháp</option>
                <option value="REJECTED">Bị từ chối</option>
              </Form.Select>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAdd}
              >
                <Plus size={18} className="me-1" />
                Thêm món
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div
                className="spinner-border text-primary"
                role="status"
              >
                <span className="visually-hidden">Đang tải...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex flex-column gap-3">
                {menuItems.map((item) => {
                  const category = categories.find((c) => c.id === item.categoryId);

                  return (
                    <div
                      key={item.id}
                      className="d-flex align-items-center gap-3 p-3 border rounded bg-light"
                    >
                      <div className="cursor-grab d-flex align-items-center">
                        <GripVertical
                          size={20}
                          className="text-muted"
                        />
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <ImagePreview
                          src={item.image || '/images/placeholder.jpg'}
                          alt={item.name}
                          size={56}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <h5 className="mb-0">{item.name}</h5>
                            {item.isFeatured && (
                              <Badge bg="warning" text="dark">Nổi bật</Badge>
                            )}
                            {item.requires_training && (
                              <Badge bg="primary">📚 Cần training</Badge>
                            )}
                            {item.training_auto_locked && (
                              <Badge bg="danger">🔒 Auto-lock</Badge>
                            )}
                            {!item.isAvailable && <Badge bg="danger">Hết hàng</Badge>}
                            {item.approval_status && item.approval_status !== 'APPROVED' && (
                              <Badge bg={APPROVAL_STATUS_LABELS[item.approval_status]?.bg || 'secondary'}>
                                {APPROVAL_STATUS_LABELS[item.approval_status]?.label || item.approval_status}
                              </Badge>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Badge
                              bg="info"
                              pill
                            >
                              {category?.name || 'N/A'}
                            </Badge>
                            <span className="text-muted small">{item.price.toLocaleString('vi-VN')}đ</span>
                            {item.creator && (
                              <span className="text-muted small">
                                👤 {item.creator.full_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {item.approval_status === 'APPROVED' && (
                          <Button
                            variant={item.released_at ? 'outline-success' : 'warning'}
                            size="sm"
                            title="Release Hub"
                            onClick={() => setReleaseModal({ show: true, id: item.id, name: item.name })}
                          >
                            🚀 {item.released_at ? 'Released' : 'Release'}
                          </Button>
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(item)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {menuItems.length === 0 && (
                <div className="text-center py-5 text-muted">
                  {filterCategory === 'all'
                    ? 'Chưa có món nào. Hãy thêm món đầu tiên!'
                    : 'Không có món nào trong danh mục này.'}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <MenuItemModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingItem(null);
        }}
        menuItem={editingItem}
        categories={categories}
        modifierGroups={modifierGroups}
        onSave={handleSave}
      />
      <ReleaseHubModal
        show={releaseModal.show}
        onHide={() => setReleaseModal({ show: false, id: null, name: '' })}
        menuItemId={releaseModal.id}
        menuItemName={releaseModal.name}
        onGoToRecipe={handleGoToRecipe}
      />
        </Tab>

        <Tab eventKey="pending" title={<span>Chờ duyệt 🔔</span>}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h4 className="mb-0">Món chờ duyệt</h4>
              <small className="text-muted">Món do nhân viên tạo, cần admin phê duyệt mới mở bán</small>
            </Card.Header>
            <Card.Body>
              <PendingItemsPanel categories={categories} onGoToRecipe={handleGoToRecipe} />
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="training" title={<span>📚 Đào tạo</span>}>
          <Tabs defaultActiveKey="releases" className="mb-3 mt-2 border-bottom-0" variant="pills">
            <Tab eventKey="releases" title="📋 Phát hành">
              <TrainingReleasesTab categories={categories} />
            </Tab>
            <Tab eventKey="progress" title="📊 Tiến độ training">
              <TrainingProgressPanel />
            </Tab>
          </Tabs>
        </Tab>
      </Tabs>
    </>
  );
};

// ---- Training Releases Tab ----
const TrainingReleasesTab = ({ categories }) => {
  const [trainableItems, setTrainableItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); // chỉ true khi đang chạy thao tác hàng loạt (thao tác đơn lật lạc quan, không chặn)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, branchesRes, releasesRes] = await Promise.all([
        menuItemAPI.getAllMenuItems({ approval_status: 'APPROVED', limit: 200 }),
        branchAPI.getBranches(),
        menuItemAPI.getReleases(),
      ]);
      const allItems = (itemsRes?.data || itemsRes || []);
      setTrainableItems(allItems.filter((i) => i.requires_training));
      setBranches(branchesRes?.data || branchesRes || []);
      setReleases(releasesRes?.data || releasesRes || []);
    } catch {
      toast.error('Không tải được dữ liệu đào tạo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh ngầm chỉ danh sách releases — KHÔNG bật loading (không remount bảng, giữ vị trí cuộn)
  const refreshReleases = useCallback(async () => {
    try {
      const releasesRes = await menuItemAPI.getReleases();
      setReleases(releasesRes?.data || releasesRes || []);
    } catch {
      /* giữ state hiện tại nếu refresh lỗi */
    }
  }, []);

  const isReleased = (itemId, branchId) =>
    releases.some((r) => r.menu_item_id === itemId && r.branch_id === branchId && r.is_active);

  const getReleaseId = (itemId, branchId) =>
    releases.find((r) => r.menu_item_id === itemId && r.branch_id === branchId && r.is_active)?.id;

  // Cập nhật lạc quan local releases để switch lật ngay, không chờ server
  const applyLocal = (pairs, release) => {
    setReleases((prev) => {
      let next = [...prev];
      for (const { itemId, branchId } of pairs) {
        const idx = next.findIndex(
          (r) => r.menu_item_id === itemId && r.branch_id === branchId && r.is_active,
        );
        if (release && idx === -1) {
          next.push({
            id: `temp-${itemId}-${branchId}`,
            menu_item_id: itemId,
            branch_id: branchId,
            is_active: true,
          });
        } else if (!release && idx !== -1) {
          next = next.filter((_, i) => i !== idx);
        }
      }
      return next;
    });
  };

  const allReleasedForBranch = (branchId) =>
    trainableItems.length > 0 && trainableItems.every((i) => isReleased(i.id, branchId));

  const allReleasedForItem = (itemId) =>
    branches.length > 0 && branches.every((b) => isReleased(itemId, b.id));

  /**
   * Chạy thao tác hàng loạt trên danh sách cặp (itemId, branchId).
   * release=true: phát hành các ô chưa phát hành. release=false: thu hồi các ô đang phát hành.
   */
  const runBulk = async (pairs, release, successMsg) => {
    const targets = pairs.filter(({ itemId, branchId }) =>
      release ? !isReleased(itemId, branchId) : isReleased(itemId, branchId),
    );
    if (targets.length === 0) {
      toast.info(release ? 'Tất cả đã được phát hành' : 'Không có gì để thu hồi');
      return;
    }
    const isBulk = targets.length > 1;
    // Lấy releaseId TRƯỚC khi cập nhật lạc quan (để thu hồi)
    const revokeIds = release ? [] : targets.map((t) => getReleaseId(t.itemId, t.branchId));

    applyLocal(targets, release); // switch lật ngay
    if (isBulk) setBusy(true);
    try {
      const results = await Promise.allSettled(
        targets.map((t, i) =>
          release
            ? menuItemAPI.createRelease(t.itemId, t.branchId)
            : menuItemAPI.deleteRelease(revokeIds[i]),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      await refreshReleases(); // đồng bộ ngầm (lấy id thật), KHÔNG remount bảng
      if (failed > 0) {
        toast.warning(`${successMsg} (${targets.length - failed}/${targets.length} OK, ${failed} lỗi)`);
      } else if (isBulk) {
        toast.success(`${successMsg} (${targets.length} mục)`);
      } else {
        toast.success(successMsg);
      }
    } catch (err) {
      await refreshReleases(); // revert về trạng thái server nếu lỗi
      toast.error(err?.response?.data?.message || 'Thao tác thất bại');
    } finally {
      if (isBulk) setBusy(false);
    }
  };

  const allPairs = () =>
    trainableItems.flatMap((i) => branches.map((b) => ({ itemId: i.id, branchId: b.id })));

  const handleToggleCell = (item, branch) =>
    runBulk(
      [{ itemId: item.id, branchId: branch.id }],
      !isReleased(item.id, branch.id),
      isReleased(item.id, branch.id)
        ? `Đã thu hồi "${item.name}" khỏi ${branch.name}`
        : `Đã phát hành "${item.name}" cho ${branch.name}`,
    );

  const handleToggleBranch = (branch) => {
    const release = !allReleasedForBranch(branch.id);
    runBulk(
      trainableItems.map((i) => ({ itemId: i.id, branchId: branch.id })),
      release,
      release ? `Đã phát hành tất cả món cho ${branch.name}` : `Đã thu hồi tất cả món khỏi ${branch.name}`,
    );
  };

  const handleToggleItem = (item) => {
    const release = !allReleasedForItem(item.id);
    runBulk(
      branches.map((b) => ({ itemId: item.id, branchId: b.id })),
      release,
      release ? `Đã phát hành "${item.name}" cho tất cả chi nhánh` : `Đã thu hồi "${item.name}" khỏi tất cả chi nhánh`,
    );
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" size="sm" /> Đang tải...</div>;

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white border-bottom">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          <div>
            <h4 className="mb-1">📚 Phát hành món đào tạo theo chi nhánh</h4>
            <small className="text-muted">
              Bật ô để phát hành món cho chi nhánh — NV nhận thông báo học khi check-in. Tắt để thu hồi.
            </small>
          </div>
          {trainableItems.length > 0 && (
            <div className="d-flex gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline-primary"
                disabled={busy}
                onClick={() => runBulk(allPairs(), true, 'Đã phát hành toàn bộ')}
              >
                {busy ? <Spinner animation="border" size="sm" /> : 'Phát hành tất cả'}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={busy}
                onClick={() => runBulk(allPairs(), false, 'Đã thu hồi toàn bộ')}
              >
                Thu hồi tất cả
              </Button>
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {trainableItems.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>Chưa có món nào được đánh dấu "Cần đào tạo".</p>
            <p className="small">Mở dialog sửa món và bật toggle "📚 Cần đào tạo nhân viên".</p>
          </div>
        ) : (
          <Table bordered hover responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 220 }}>
                  Món
                  <div className="text-muted fw-normal" style={{ fontSize: 10 }}>
                    Bấm tên cột/hàng để bật-tắt cả nhóm
                  </div>
                </th>
                {branches.map((b) => (
                  <th key={b.id} className="text-center align-middle" style={{ minWidth: 130 }}>
                    <div>{b.name}</div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      style={{ fontSize: 11 }}
                      disabled={busy}
                      onClick={() => handleToggleBranch(b)}
                      title={allReleasedForBranch(b.id) ? 'Thu hồi tất cả món của CN này' : 'Phát hành tất cả món cho CN này'}
                    >
                      {allReleasedForBranch(b.id) ? '✕ Bỏ tất cả' : '✓ Chọn tất cả'}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trainableItems.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {item.image && (
                        <img src={item.image} alt={item.name} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                      )}
                      <div className="flex-grow-1">
                        <div className="fw-semibold" style={{ fontSize: 13 }}>{item.name}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{item.category?.name}</div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none shrink-0"
                        style={{ fontSize: 11 }}
                        disabled={busy}
                        onClick={() => handleToggleItem(item)}
                        title={allReleasedForItem(item.id) ? 'Thu hồi khỏi tất cả CN' : 'Phát hành cho tất cả CN'}
                      >
                        {allReleasedForItem(item.id) ? '✕ Bỏ hết' : '✓ Tất cả CN'}
                      </button>
                    </div>
                  </td>
                  {branches.map((branch) => {
                    const released = isReleased(item.id, branch.id);
                    return (
                      <td key={branch.id} className="text-center align-middle">
                        <Form.Check
                          type="switch"
                          id={`release-${item.id}-${branch.id}`}
                          checked={released}
                          disabled={busy}
                          onChange={() => handleToggleCell(item, branch)}
                          label=""
                          title={released ? `Thu hồi khỏi ${branch.name}` : `Phát hành cho ${branch.name}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

// ---- Training Progress Panel ----
const SKILL_LABEL = {
  CONFIRMED: 'Đã xác nhận',
  SUBMITTED: 'Chờ xác nhận',
  REJECTED: 'Từ chối',
  IN_PROGRESS: 'Đang học',
  NOT_STARTED: 'Chưa học',
};
const SKILL_COLOR = {
  CONFIRMED: { bg: '#d1fae5', text: '#065f46' },
  SUBMITTED: { bg: '#fef3c7', text: '#92400e' },
  REJECTED:  { bg: '#fee2e2', text: '#991b1b' },
  IN_PROGRESS: { bg: '#dbeafe', text: '#1e40af' },
  NOT_STARTED: { bg: '#f1f5f9', text: '#475569' },
};

const EmpInitials = ({ name, size = 32 }) => {
  const initials = getAvatarInitials(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#e0e7ff',
      color: '#4f46e5', fontWeight: 700, fontSize: size * 0.38,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
};

const StarRating = ({ rating }) => (
  <span style={{ color: '#f59e0b', letterSpacing: -1 }}>
    {[1, 2, 3, 4, 5].map((s) => (
      s <= rating
        ? <StarFill key={s} size={11} />
        : <Star key={s} size={11} style={{ opacity: 0.25 }} />
    ))}
  </span>
);

const PANEL_HEIGHT = 580;

const TrainingProgressPanel = () => {
  const [branches, setBranches] = useState([]);
  const [trainableItems, setTrainableItems] = useState([]);
  // #175019 — chi nhánh của bảng tiến độ đào tạo lấy từ thanh trên đầu, bỏ ô chọn riêng.
  const { selectedBranch: headerBranch } = useBranch();
  const selectedBranch = headerBranch?.id || '';
  const [selectedItem, setSelectedItem] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [branchRes, itemsRes] = await Promise.all([
          branchAPI.getBranches(),
          menuItemAPI.getAllMenuItems({ approval_status: 'APPROVED', limit: 200 }),
        ]);
        const branchList = branchRes?.data || branchRes || [];
        const allItems = itemsRes?.data || itemsRes || [];
        setBranches(branchList);
        setTrainableItems(allItems.filter((i) => i.requires_training));
      } catch {
        toast.error('Không tải được dữ liệu');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadDashboard = useCallback(async (item, branchId) => {
    setSelectedItem(item);
    setHistoryEmployee(null);
    setHistoryData(null);
    setLoadingDashboard(true);
    // Keep old dashboardData visible while loading — prevents layout jitter
    try {
      const res = await menuItemAPI.getTrainingDashboard(item.id, branchId);
      setDashboardData(res);
    } catch (err) {
      toast.error(err?.message || 'Không tải được dashboard');
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  const loadHistory = useCallback(async (menuItemId, employee) => {
    setHistoryEmployee(employee);
    setHistoryData(null);
    setLoadingHistory(true);
    try {
      const res = await menuItemAPI.getItemSkillHistory(menuItemId, employee.id);
      setHistoryData(res?.data || []);
    } catch {
      toast.error('Không tải được lịch sử');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Đổi chi nhánh ở thanh trên đầu → nạp lại bảng của đúng món đang mở.
  useEffect(() => {
    if (selectedItem) loadDashboard(selectedItem, selectedBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: PANEL_HEIGHT }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (trainableItems.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: 300 }}>
        <PersonCheck size={40} className="mb-3 opacity-25" />
        <p className="mb-1">Chưa có món nào được đánh dấu "Cần đào tạo".</p>
        <small>Mở dialog sửa món và bật toggle "📚 Cần đào tạo nhân viên".</small>
      </div>
    );
  }

  const coveragePct = dashboardData ? Math.round(dashboardData.coverage * 100) : 0;
  const branchName = branches.find((b) => b.id === selectedBranch)?.name || '';
  const isOilSpread = dashboardData?.flow === 'OIL_SPREAD';

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, height: PANEL_HEIGHT }}>
        {/* ── Left: item list ── */}
        <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
          {/* #175019 — chỉ HIỂN THỊ chi nhánh đang chọn ở thanh trên đầu, không cho chọn lại ở đây. */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {branchName || 'Chưa chọn chi nhánh — chọn ở thanh trên đầu'}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
              {trainableItems.length} món cần training
            </div>
          </div>

          {/* Item list — scrollable */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {trainableItems.map((item) => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => !isSelected && loadDashboard(item, selectedBranch)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', border: 'none', borderBottom: '1px solid #f1f5f9',
                    textAlign: 'left', cursor: isSelected ? 'default' : 'pointer',
                    background: isSelected ? '#eff6ff' : 'transparent',
                    borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}
                >
                  {item.image ? (
                    <img src={item.image} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 34, height: 34, borderRadius: 6, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PersonCheck size={16} color="#4f46e5" />
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#1d4ed8' : '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.category?.name}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Right: dashboard ── */}
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
          {/* Loading overlay — no unmount, no jitter */}
          {loadingDashboard && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 8 }}>
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          )}

          {!selectedItem ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <PersonCheck size={40} className="mb-3 opacity-25" />
              <p style={{ fontSize: 14 }}>Chọn một món để xem tiến độ training</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {dashboardData?.menu_item.name ?? selectedItem.name}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span style={{ fontSize: 12, color: '#64748b' }}>{branchName}</span>
                      {dashboardData && (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                          background: isOilSpread ? '#dbeafe' : '#e0e7ff',
                          color: isOilSpread ? '#1d4ed8' : '#4338ca',
                        }}>
                          {isOilSpread ? 'Dầu loang' : 'Cùng ca'}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm" variant="light"
                    style={{ flexShrink: 0, padding: '4px 8px' }}
                    onClick={() => loadDashboard(selectedItem, selectedBranch)}
                    disabled={loadingDashboard}
                    title="Làm mới"
                  >
                    <ArrowRepeat size={14} />
                  </Button>
                </div>

                {dashboardData && (
                  <>
                    {/* Coverage bar */}
                    <div style={{ marginTop: 12 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#475569' }}>Độ phủ kỹ năng</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: coveragePct >= 70 ? '#16a34a' : '#d97706' }}>
                          {coveragePct}%
                          <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 4, color: '#94a3b8' }}>
                            ({dashboardData.counts.confirmed}/{dashboardData.counts.total} NV)
                          </span>
                        </span>
                      </div>
                      <div style={{ height: 6, borderRadius: 99, background: '#e2e8f0', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 99,
                          width: `${coveragePct}%`,
                          background: coveragePct >= 70 ? '#22c55e' : '#f59e0b',
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                    </div>

                    {/* Stat chips */}
                    <div className="d-flex gap-2 mt-3 flex-wrap">
                      {[
                        { label: 'Đã xác nhận', val: dashboardData.counts.confirmed, ...SKILL_COLOR.CONFIRMED },
                        { label: 'Chờ xác nhận', val: dashboardData.counts.submitted, ...SKILL_COLOR.SUBMITTED },
                        { label: 'Từ chối',      val: dashboardData.counts.rejected,  ...SKILL_COLOR.REJECTED  },
                        { label: 'Chưa học',     val: dashboardData.counts.not_started, ...SKILL_COLOR.NOT_STARTED },
                      ].map(({ label, val, bg, text }) => (
                        <div key={label} style={{ background: bg, color: text, borderRadius: 8, padding: '6px 12px', textAlign: 'center', minWidth: 80 }}>
                          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{val}</div>
                          <div style={{ fontSize: 10, fontWeight: 500, marginTop: 1 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Employee table */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {dashboardData ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                        <th style={{ padding: '8px 14px', fontWeight: 600, color: '#475569', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Nhân viên</th>
                        <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Trạng thái</th>
                        <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Ngày / Đánh giá</th>
                        <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', width: 32 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.employees.map((emp) => {
                        const sc = SKILL_COLOR[emp.skill_status] ?? SKILL_COLOR.NOT_STARTED;
                        const dateStr = emp.confirmed_at
                          ? new Date(emp.confirmed_at).toLocaleDateString('vi-VN')
                          : emp.last_submitted_at
                            ? `Nộp ${new Date(emp.last_submitted_at).toLocaleDateString('vi-VN')}`
                            : null;
                        return (
                          <tr key={emp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 14px' }}>
                              <div className="d-flex align-items-center gap-2">
                                <EmpInitials name={emp.full_name} size={28} />
                                <div>
                                  <div style={{ fontWeight: 500, color: '#0f172a', lineHeight: 1.3 }}>{emp.full_name}</div>
                                  {emp.phone_number && (
                                    <a href={`tel:${emp.phone_number}`} style={{ fontSize: 11, color: '#64748b', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                                      <Telephone size={10} /> {emp.phone_number}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                              <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, whiteSpace: 'nowrap' }}>
                                {SKILL_LABEL[emp.skill_status] ?? emp.skill_status}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                                {dateStr ?? <span style={{ color: '#cbd5e1' }}>—</span>}
                              </div>
                              {emp.last_rating > 0 && (
                                <StarRating rating={emp.last_rating} />
                              )}
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              {emp.skill_status !== 'NOT_STARTED' && (
                                <button
                                  type="button"
                                  onClick={() => loadHistory(dashboardData.menu_item.id, emp)}
                                  title="Xem lịch sử review"
                                  style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}
                                >
                                  <ClockHistory size={12} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  !loadingDashboard && (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#cbd5e1', fontSize: 13 }}>
                      Không có dữ liệu
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Evidence history modal ── */}
      {historyEmployee && (
        <Modal show onHide={() => { setHistoryEmployee(null); setHistoryData(null); }} size="lg" centered>
          <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
            <div>
              <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                Lịch sử training
              </Modal.Title>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                {historyEmployee.full_name} · {selectedItem?.name}
              </div>
            </div>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 20px' }}>
            {loadingHistory ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            ) : !historyData || historyData.length === 0 ? (
              <div className="d-flex flex-column align-items-center py-4 text-muted">
                <ClockHistory size={32} className="mb-2 opacity-25" />
                <p style={{ fontSize: 14 }}>Chưa có lịch sử review nào</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {historyData.map((review, idx) => {
                  const isConfirmed = review.result === 'CONFIRMED';
                  return (
                    <div key={review.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                      {/* Review header */}
                      <div style={{ padding: '10px 14px', background: isConfirmed ? '#f0fdf4' : '#fff1f2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{
                            width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            background: isConfirmed ? '#bbf7d0' : '#fecdd3',
                          }}>
                            {isConfirmed
                              ? <CheckCircle size={14} color="#16a34a" />
                              : <XCircle size={14} color="#dc2626" />
                            }
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: isConfirmed ? '#15803d' : '#dc2626' }}>
                            {isConfirmed ? 'Đã xác nhận' : 'Từ chối'}
                          </span>
                          {review.reviewer && (
                            <span style={{ fontSize: 12, color: '#64748b' }}>bởi {review.reviewer.full_name}</span>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {review.rating > 0 && <div style={{ marginBottom: 2 }}><StarRating rating={review.rating} /></div>}
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {new Date(review.created_at).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>

                      {/* Feedback + photos */}
                      {(review.feedback || review.assignment?.proof_photo_urls?.length > 0) && (
                        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {review.feedback && (
                            <p style={{ fontSize: 13, color: '#475569', fontStyle: 'italic', margin: 0 }}>
                              "{review.feedback}"
                            </p>
                          )}
                          {review.assignment?.proof_photo_urls?.length > 0 && (
                            <div>
                              <div className="d-flex align-items-center gap-1 mb-2" style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                                <Images size={12} /> Bằng chứng ({review.assignment.proof_photo_urls.length} ảnh)
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {review.assignment.proof_photo_urls.map((url, i) => (
                                  <a key={i} href={url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                    <img
                                      src={url}
                                      alt={`Ảnh ${i + 1}`}
                                      style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', transition: 'opacity 0.2s' }}
                                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default AdminMenuItems;
